import {Component, OnInit, ViewChild} from '@angular/core';
import {HELP_FOLDERS, MSG_KEY, MSG_SEVERITY} from "../../core/constants";
import {saveAs} from "file-saver";
import {confirm} from "devextreme/ui/dialog";
import {ResponseWrapper} from "../../core/suppliers/wrappers/response-wrapper";
import {IsDeletableObject} from "../../core/models/is-deletable-object";
import {Auth2Service} from "../../core/services/security/auth2.service";
import {InternationalizationService} from "../../core/services/i8n/i8n.service";
import {GraphQLService} from "../../core/services/technique/graphql.service";
import {UtilsService} from "../../core/utils/utils.service";
import {ToastService} from "../../core/services/technique/toast.service";
import {Subscription} from "rxjs";
import {AllergeneDTO} from "../../core/dtos/allergene-dto";
import {AllergeneSavedSupplier, AllergenesService} from "../../core/services/entities/allergenes-service.service";
import {ImageSize} from "../../core/enums/image-size-enum";
import {DxTooltipComponent} from "devextreme-angular";
import {ButtonThemeEnum} from "../../shared/ui/button/button-theme-enum";

@Component({
    selector: 'yo-allergenes',
    templateUrl: './allergenes.component.html',
    styleUrls: ['./allergenes.component.scss'],
    standalone: false
})
export class AllergenesComponent implements OnInit {

  public readonly ButtonThemeEnum = ButtonThemeEnum;

  @ViewChild("importTooltip") importTooltip: DxTooltipComponent;

  subLog: Subscription;
  subAllergeneSaved: Subscription;
  subAllergenes: Subscription;
  importSub: Subscription;
  exportSub: Subscription;
  deleteSub: Subscription;

  logged: boolean = false;

  allergenes: AllergeneDTO[] = [];

  selectedRows: number[] = [];
  errors: any[] = [];

  displayImagePopup: boolean = false;
  displayImportPopupErrors: boolean = false;
  displayDeletePopupErrors: boolean = false;

  pathHelpFile: string = HELP_FOLDERS.REFERENTIEL + '/allergenes/allergenes';

  imageTitle: string = "Logo"
  imagePath: string = "";

  constructor(public allergenesSvc: AllergenesService,
              private auth2Svc: Auth2Service,
              private i8nSvc: InternationalizationService,
              private graphQlSvc: GraphQLService,
              public utilsSvc: UtilsService,
              public toastSvc: ToastService) {
  }

  ngOnInit() {
    this.initLoggedSubscription();
    this.initAllergenesSubscription();
    this.initAllergeneSavedSubscription();
  }

  ngOnDestroy(): void {
    this.utilsSvc.unsubscribe(this.subLog);
    this.utilsSvc.unsubscribe(this.subAllergeneSaved);
    this.utilsSvc.unsubscribe(this.subAllergenes);
    this.utilsSvc.unsubscribe(this.importSub);
    this.utilsSvc.unsubscribe(this.exportSub);
    this.utilsSvc.unsubscribe(this.deleteSub);
  }

  initLoggedSubscription = (): void => {
    this.subLog = this.auth2Svc.isLoggedIn$
      .subscribe(res => this.logged = res);
  }

  initAllergenesSubscription = (): void => {
    const idsSites: number[] = this.auth2Svc.utilisateur.sites.map(s => s.id);
    this.subAllergenes = this.graphQlSvc.sendQuery(`
      {
        allAllergenes(filters: {
          siteIds: [${idsSites}]
        }) {
            id,
            code,
            libelle,
            appellationInformationConsommateur,
            actif,
            site {
                id,
                libelle,
            },
        }
      }
    `)
      .subscribe((response: any) => {
        this.allergenes = response.allAllergenes;
      });
  }

  initAllergeneSavedSubscription = (): void => {
    this.subAllergeneSaved = this.allergenesSvc.allergeneSaved$
      .subscribe((response: AllergeneSavedSupplier) => {
        if (response.isUpdate) {
          const idx: number = this.allergenes.findIndex(allergene => allergene.id === response.allergene.id);
          this.allergenes[idx] = response.allergene;
        } else {
          this.allergenes.push(response.allergene);
        }
      });
  };

  openDialogEdition = (allergene: AllergeneDTO): void => {
    this.allergenesSvc.announceOpenDialogEdition(allergene);
  };

  calculateSiteValue = (allergene: AllergeneDTO): String => {
    return allergene?.site?.libelle
  };

  exportXslx = () => {
    this.exportSub = this.allergenesSvc.exportAllergeneByFilters(this.selectedRows).subscribe(response => {
      const filename = `allergene-export-${new Date().getTime()}.xlsx`;
      const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
      this.selectedRows = [];
      saveAs(blob, filename);
    });
  }

  importXslx = async ($event: any) => {
    this.errors = [];
    if ($event.target.files && $event.target.files.length) {
      const file: File = $event.target.files[0];
      this.importSub = this.allergenesSvc.importAllergeneFromXlsx(file)
        .subscribe(response => {
          const res: any = response.one;
          if (res.allElementsImported)
            this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le fichier a été importé avec succès`);
          else {
            for (const item of Object.entries(res.reports)) {
              let values: any[] = Array.of(item[1]);
              const labelsError: any[] = values.map(val => val.map(v => this.i8nSvc.getLabelFromCode(v.code, v.args.split(','))).join(', '));
              this.errors.push({infosLine: item[0], labelsError});
            }
          }
          for (const item of res.elementsImported) {
            let index = this.allergenes.findIndex(allergene => allergene.id == item.id);
            if (index >= 0)
              this.allergenes[index] = item;
            else
              this.allergenes.push(item)
          }

          if (this.errors != null && this.errors.length > 0)
            this.displayImportErrors();

          $event.target.value = "";
          this.importTooltip.instance.hide();
        });
    }
  }

  displayImportErrors = (): void => {
    this.displayImportPopupErrors = true;
  }

  displayDeleteErrors = (): void => {
    this.displayDeletePopupErrors = true;
  }

  closeErrors = (): void => {
    this.errors = [];
    this.displayImportPopupErrors = false;
    this.displayDeletePopupErrors = false;
  }


  deleteValues = async (): Promise<void> => {
    this.errors = [];
    let idsToDeleteInTable = Object.assign([], this.selectedRows);

    let isDeletionValidated: boolean = await confirm(this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? "CONFIRM_DELETION_PLURAL" : "CONFIRM_DELETION_SINGULAR", null), this.i8nSvc.getLabelFromCode("Suppression", null));

    if (!isDeletionValidated)
      return new Promise(null);

    this.deleteSub = this.allergenesSvc.deleteAllergeneByFilters(this.selectedRows).subscribe(response => {
      const res: any = (response as ResponseWrapper<IsDeletableObject>).one;

      if (res) {
        if (res.messagesErrorList && res.messagesErrorList.length > 0) {
          for (const error of res.messagesErrorList) {
            const infosLine: string = this.i8nSvc.getLabelFromCode(error.code, error.args.split(','));
            this.errors.push({infosLine});
            const elementToKeep = this.allergenes.find(allergene => allergene.code === error.args);
            idsToDeleteInTable = idsToDeleteInTable.filter(id => elementToKeep.id !== id);
          }
          this.displayDeleteErrors();
        } else {
          this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, this.i8nSvc.getLabelFromCode(idsToDeleteInTable.length > 1 ? `ALLERGENE_DELETION_COMPLETED_PLURAL` : `ALLERGENE_DELETION_COMPLETED_SINGULAR`, null));
        }
        this.allergenes = this.allergenes.filter(allergene => !idsToDeleteInTable.find(id => id === allergene.id));
      }
    });
  }

  canDelete = (): boolean => this.selectedRows.length > 0;

  getLogoUrl  = (item: AllergeneDTO): string => {
    return this.allergenesSvc.getUrl(item, ImageSize.Thumb);
  }

  openImagePopup = (item: AllergeneDTO) => {
    this.displayImagePopup = true;
    this.imageTitle = item.libelle;
    this.imagePath = this.allergenesSvc.getUrl(item, ImageSize.Full, true)
  };

  closeImagePopup = () => {
    this.imagePath = "";
    this.imageTitle = ""
    this.displayImagePopup = false;
  }

}
